"use client"

import { getInitials } from "@primoui/utils"
import { getCurrentUserAvatarProxyUrl } from "@webscraping/auth/avatar"
import { motion } from "motion/react"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/legacy/common/avatar"
import { Box } from "~/components/legacy/common/box"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/legacy/common/dropdown-menu"
import { LogOutIcon, ShieldHalfIcon } from "~/components/ui/icons"
import { NavLink } from "~/components/legacy/web/ui/nav-link"
import { UserLogout } from "~/components/web/user-logout"
import { useSession } from "~/lib/auth-client"
import { cx } from "~/lib/legacy-utils"

export const UserMenu = ({ signedOutClassName }: { signedOutClassName?: string }) => {
  const { data: session, isPending } = useSession()
  const t = useTranslations()
  // useSession resolves synchronously on the client but not during SSR; render
  // the pending placeholder until after mount so hydration sees identical HTML.
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => setIsMounted(true), [])

  if (!isMounted || isPending) {
    return (
      <span
        className={cx(
          "whitespace-nowrap p-0.5 text-sm font-medium text-muted-foreground",
          signedOutClassName,
        )}
      >
        {t("navigation.sign_in")}
      </span>
    )
  }

  if (!session?.user) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={signedOutClassName}
      >
        <NavLink href="/auth/login" className="whitespace-nowrap text-sm font-medium">
          {t("navigation.sign_in")}
        </NavLink>
      </motion.div>
    )
  }

  const avatarSrc = session.user.image?.trim() || getCurrentUserAvatarProxyUrl(24)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Box hover focus>
            <Avatar className="size-6 duration-100">
              {avatarSrc ? <AvatarImage src={avatarSrc} /> : null}
              <AvatarFallback>{getInitials(session.user.name)}</AvatarFallback>
            </Avatar>
          </Box>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuLabel className="max-w-48 truncate font-normal leading-relaxed">
            {session.user.name}

            {session.user.name !== session.user.email && (
              <div className="text-muted-foreground truncate">{session.user.email}</div>
            )}
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          {session.user.role === "admin" && (
            <DropdownMenuItem asChild>
              <NavLink href="/app" prefix={<ShieldHalfIcon />}>
                {t("navigation.admin_panel")}
              </NavLink>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem asChild>
            <NavLink prefix={<LogOutIcon />} asChild>
              <UserLogout>{t("navigation.sign_out")}</UserLogout>
            </NavLink>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </motion.div>
  )
}
